import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import styles from "./FilePreviewModal.module.scss";
import { useSelector } from "redux/hooks";
import classNames from "classnames";
import AnimatedModal from "components/AnimateModal/AnimateModal";
import FilePreviewHeader from "./Components/FilePreviewHeader";
import PreviewContent from "./Components/PreviewContent";
import PDFPreview from "./Components/previewTypes/PDFPreview"; // Your existing component
import { Preview, PreviewableFile } from "redux/actions";
import { getFileName, getFileType } from "utils/fileUtils";
import { getFilePreview } from "utils/previewGenerators";
import { externalDomains } from "utils/constants";

interface FilePreviewModalProps {
  file: PreviewableFile | null;
  onClose: () => void;
  getsize?: boolean;
}

// Your existing utility functions
const hasFileExtension = (fileName: string): boolean => {
  return /\.[^/.]+$/.test(fileName);
};

const isExternalWebsite = (url: string): boolean => {
  if (!url) return false;
  // Your existing implementation
  try {
    const lastSegment = url.split('/').pop() || '';
    const fileExtension = lastSegment.split('.').pop() || '';
    return externalDomains.includes(fileExtension.toLowerCase());
  } catch (e) {
    console.error("Invalid URL:", url, e);
    return false;
  }
};

// Function to check if file is PDF
const isPDF = (fileName: string): boolean => {
  if (!fileName) return false;
  return fileName.toLowerCase().endsWith('.pdf');
};

// Improved FilePreviewModalContent component
const FilePreviewModalContent = React.memo(({ file, onClose, getsize }: FilePreviewModalProps) => {
  const [preview, setPreview] = useState<Preview>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lineCount, setLineCount] = useState<number | null>(null);
  
  const { theme } = useSelector((state) => state.authReducer);

  // Memoize file properties to prevent unnecessary recalculations
  const { fileName, fileUrl, fileSize, isPdfFile } = useMemo(() => {
    const name = file ? getFileName(file) : "";
    const url = file?.S3Link || "";
    const size = file && 'fileSize' in file ? file.fileSize : undefined;
    const isPdf = isPDF(name);
    return { fileName: name, fileUrl: url, fileSize: size, isPdfFile: isPdf };
  }, [file]);

  // Always memoize PDF component, but only return it when needed
  const pdfComponent = useMemo(() => {
    return <PDFPreview url={fileUrl}/>;
  }, [fileUrl]);

  // Only generate preview if needed (not for PDFs)
  useEffect(() => {
    if (!file || isPdfFile) {
      setLoading(false);
      return;
    }
    
    const generatePreview = async () => {
      setLoading(true);
      
      try {
        const fileType = getFileType(file);
        
        if (!fileUrl) {
          setError("File URL not available");
          setLoading(false);
          return;
        }
        
        const { preview, lines } = await getFilePreview(fileUrl, fileType);
        setPreview(preview);
        setLineCount(lines);
      } catch (err) {
        console.error("Error generating preview:", err);
        setError("Failed to generate preview");
      } finally {
        setLoading(false);
      }
    };
    
    generatePreview();
  }, [file, fileUrl, isPdfFile]);
  
  return (
    <AnimatedModal filePreview={true} isOpen={true} onClose={onClose}>
      <div 
        className={classNames(styles.fullscreenModalContainer, {
          [styles.light]: theme === "light",
          [styles.dark]: theme === "dark",
        })}
      >
        <FilePreviewHeader 
          fileName={fileName} 
          fileSize={fileSize} 
          lineCount={isPdfFile ? null : lineCount} 
          onClose={onClose} 
          theme={theme}
          getsize={getsize}
        />
        
        {isPdfFile ? (
          // Use the memoized PDF component
          pdfComponent
        ) : (
          <PreviewContent 
          fileName={fileName} 
            loading={loading}
            error={error}
            preview={preview}
            fileUrl={fileUrl}
            theme={theme}
          />
        )}
      </div>
    </AnimatedModal>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the file S3Link changes
  const prevFile = prevProps.file;
  const nextFile = nextProps.file;
  
  if (!prevFile && !nextFile) return true;
  if (!prevFile || !nextFile) return false;
  
  // Use S3Link for comparison since that's what matters for display
  return prevFile.S3Link === nextFile.S3Link;
});

// Set displayName for debugging
FilePreviewModalContent.displayName = 'FilePreviewModalContent';

// Main component with minimal changes
const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ file, onClose, getsize }) => {
  // Create a stable key for the modal content - move this outside conditional logic
  const stableKey = useMemo(() => {
    if (!file?.S3Link) return 'empty-modal';
    return `modal-${file.S3Link.split('/').pop()?.split('?')[0]}`;
  }, [file?.S3Link]);

  const handleOpenInNewTab = useCallback(() => {
    if (!file || !file.S3Link) return;
    
    const fileName = getFileName(file);
    
    if (!hasFileExtension(fileName) || isExternalWebsite(file.S3Link)) {
      window.open(file.S3Link, '_blank');
      onClose();
    }
  }, [file, onClose]);

  useEffect(() => {
    if (!file) return;
    
    handleOpenInNewTab();

    // Only handle overflow if we're showing the modal
    if (hasFileExtension(getFileName(file)) && !isExternalWebsite(file.S3Link ?? "")) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      // Re-enable when component unmounts
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [file, onClose, handleOpenInNewTab]);

  // If file has no extension, don't render the modal at all
  if (file && (!hasFileExtension(getFileName(file)) || isExternalWebsite(file.S3Link ?? ""))) {
    return null;
  }
  
  // Render the modal content in a portal at the document body level
  return ReactDOM.createPortal(
    <FilePreviewModalContent key={stableKey} file={file} onClose={onClose} getsize={getsize} />,
    document.body
  );
};

export default React.memo(FilePreviewModal);
